import React, { useState, useEffect, useCallback, useMemo } from "react";
import api, { errMsg } from "../api";
import { useAuth } from "../AuthContext";
import { t, LOCALES } from "../i18n";
import { ChevronLeft, ChevronRight, Plus, Calendar as CalIcon, Plane, Siren, Trash2, Loader2, Filter, RotateCcw, GraduationCap } from "lucide-react";
import TaskDetailDialog from "../components/TaskDetailDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { toast } from "sonner";
import { OnCallEditDialog, OnCallSwapDialog, OnCallSwapInbox } from "../components/OnCallSwap";

const TR_MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const EN_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DE_MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
const TR_DAYS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
const EN_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DE_DAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

// Sort: Bereitschaft (on-call) immer zuerst, dann übrige Kinds in fester Reihenfolge.
const KIND_ORDER = { oncall: 0, vacation: 1, public_holiday: 2, event: 3, task: 4, todo: 5, invoice: 6, holiday: 7 };
const itemSortValue = (it) => (KIND_ORDER[it.kind] ?? 99);

// Filter-Defaults: alles an, Bundesländer leer, Auslands-Feiertage aus.
const DEFAULT_FILTERS = {
    show: { oncall: true, vacation: true, event: true, task: true, todo: true, invoice: true },
    states: [],   // ISO Codes wie "HE", "BY"; leer = keine Schulferien anzeigen
    countries: [], // z. B. ["GR"] für Griechenland-Feiertage
};
const FILTERS_STORAGE_KEY = "taskflow_calendar_filters_v1";

function loadFiltersFromStorage() {
    try {
        const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
        if (!raw) return DEFAULT_FILTERS;
        const parsed = JSON.parse(raw);
        return {
            show: { ...DEFAULT_FILTERS.show, ...(parsed.show || {}) },
            states: Array.isArray(parsed.states) ? parsed.states : [],
            countries: Array.isArray(parsed.countries) ? parsed.countries : [],
        };
    } catch { return DEFAULT_FILTERS; }
}

const PUBLIC_HOLIDAY_COUNTRIES = [
    { code: "GR", name: "Griechenland", flag: "🇬🇷" },
];

const BUNDESLAENDER = [
    { code: "BW", name: "Baden-Württemberg" },
    { code: "BY", name: "Bayern" },
    { code: "BE", name: "Berlin" },
    { code: "BB", name: "Brandenburg" },
    { code: "HB", name: "Bremen" },
    { code: "HH", name: "Hamburg" },
    { code: "HE", name: "Hessen" },
    { code: "MV", name: "Mecklenburg-Vorpommern" },
    { code: "NI", name: "Niedersachsen" },
    { code: "NW", name: "Nordrhein-Westfalen" },
    { code: "RP", name: "Rheinland-Pfalz" },
    { code: "SL", name: "Saarland" },
    { code: "SN", name: "Sachsen" },
    { code: "ST", name: "Sachsen-Anhalt" },
    { code: "SH", name: "Schleswig-Holstein" },
    { code: "TH", name: "Thüringen" },
];

const EVENT_COLORS = {
    blue: "#3b82f6",
    green: "#10b981",
    red: "#ef4444",
    amber: "#f59e0b",
    purple: "#a855f7",
};

export default function CalendarPage() {
    const { lang, user } = useAuth();
    const [tasks, setTasks] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [events, setEvents] = useState([]);
    const [todos, setTodos] = useState([]);
    const [onCall, setOnCall] = useState([]);
    const [vacations, setVacations] = useState([]);
    const [spaces, setSpaces] = useState([]);
    const [users, setUsers] = useState([]);
    const [spaceFilter, setSpaceFilter] = useState("all");
    const [cursor, setCursor] = useState(new Date());
    const [openTaskId, setOpenTaskId] = useState(null);
    const [newEventOpen, setNewEventOpen] = useState(false);
    const [newOnCallOpen, setNewOnCallOpen] = useState(false);
    const [eventPresetDate, setEventPresetDate] = useState(null);
    const [filters, setFilters] = useState(loadFiltersFromStorage);
    const [filterOpen, setFilterOpen] = useState(false);
    const [schoolHolidays, setSchoolHolidays] = useState([]);   // [{name,start,end,state}]
    const [publicHolidays, setPublicHolidays] = useState([]);   // [{name,start,end,country}]
    const [dayDetailDate, setDayDetailDate] = useState(null);   // when set, the day-detail dialog opens
    const [editOnCall, setEditOnCall] = useState(null);
    const [swapOnCall, setSwapOnCall] = useState(null);

    // Persist filter changes
    useEffect(() => {
        try { localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters)); } catch { /* storage blocked */ }
    }, [filters]);

    const toggleKind = (kind) => setFilters((f) => ({ ...f, show: { ...f.show, [kind]: !f.show[kind] } }));
    const toggleState = (code) => setFilters((f) => {
        const has = f.states.includes(code);
        return { ...f, states: has ? f.states.filter((c) => c !== code) : [...f.states, code] };
    });
    const toggleCountry = (code) => setFilters((f) => {
        const has = f.countries.includes(code);
        return { ...f, countries: has ? f.countries.filter((c) => c !== code) : [...f.countries, code] };
    });
    const resetFilters = () => setFilters(DEFAULT_FILTERS);

    // Lazy-load school holidays whenever the year or selected states change
    useEffect(() => {
        if (!filters.states || filters.states.length === 0) {
            setSchoolHolidays([]);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const year = cursor.getFullYear();
                const { data } = await api.get(`/calendar/school-holidays?year=${year}&states=${filters.states.join(",")}`);
                if (!cancelled) setSchoolHolidays(Array.isArray(data) ? data : []);
            } catch {
                if (!cancelled) setSchoolHolidays([]);
            }
        })();
        return () => { cancelled = true; };
    }, [filters.states, cursor]);

    // Lazy-load public holidays for selected countries (e.g. Greece)
    useEffect(() => {
        if (!filters.countries || filters.countries.length === 0) {
            setPublicHolidays([]);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const year = cursor.getFullYear();
                const all = await Promise.all(filters.countries.map((c) =>
                    api.get(`/calendar/public-holidays?year=${year}&country=${c}`).then((r) => r.data).catch(() => [])
                ));
                if (!cancelled) setPublicHolidays(all.flat());
            } catch {
                if (!cancelled) setPublicHolidays([]);
            }
        })();
        return () => { cancelled = true; };
    }, [filters.countries, cursor]);

    const isPriv = user?.role === "admin" || user?.role === "master";

    const load = useCallback(async () => {
        const params = spaceFilter !== "all" ? `?space_id=${spaceFilter}` : "";
        const [tk, sp, u, inv, ev, td, oc, vac] = await Promise.all([
            api.get(`/calendar${params}`),
            api.get("/spaces"),
            api.get("/users"),
            api.get("/invoices").catch(() => ({ data: [] })),
            api.get("/calendar/events"),
            api.get("/todos"),
            api.get("/on-call"),
            api.get("/vacation/requests"),
        ]);
        setTasks(tk.data || []);
        setInvoices((inv.data || []).filter((iv) => iv.due_date));
        setEvents(ev.data || []);
        setTodos(td.data || []);
        setOnCall(oc.data || []);
        setVacations((vac.data || []).filter((v) => v.status === "approved"));
        setSpaces(sp.data);
        setUsers(u.data);
    }, [spaceFilter]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const h = () => load();
        window.addEventListener("taskflow:task-changed", h);
        return () => window.removeEventListener("taskflow:task-changed", h);
    }, [load]);

    const months = lang === "tr" ? TR_MONTHS : lang === "de" ? DE_MONTHS : EN_MONTHS;
    const monthLabel = months[cursor.getMonth()] + " " + cursor.getFullYear();
    const dayNames = lang === "tr" ? TR_DAYS : lang === "de" ? DE_DAYS : EN_DAYS;

    const grid = useMemo(() => {
        const y = cursor.getFullYear();
        const m = cursor.getMonth();
        const firstDay = new Date(y, m, 1);
        const startWeekday = (firstDay.getDay() + 6) % 7;
        const daysInMonth = new Date(y, m + 1, 0).getDate();
        const cells = [];
        for (let i = 0; i < startWeekday; i++) cells.push(null);
        for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, m, d));
        while (cells.length % 7 !== 0) cells.push(null);
        return cells;
    }, [cursor]);

    // ISO 8601 week number
    const isoWeekNumber = (date) => {
        const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const dayNr = (target.getUTCDay() + 6) % 7;
        target.setUTCDate(target.getUTCDate() - dayNr + 3);
        const firstThursday = target.valueOf();
        target.setUTCMonth(0, 1);
        if (target.getUTCDay() !== 4) {
            target.setUTCMonth(0, 1 + ((4 - target.getUTCDay()) + 7) % 7);
        }
        return 1 + Math.ceil((firstThursday - target) / 604800000);
    };

    // Build week rows: each row = { kw, cells: [7] }
    const weekRows = useMemo(() => {
        const rows = [];
        for (let i = 0; i < grid.length; i += 7) {
            const cells = grid.slice(i, i + 7);
            // Use the first non-null cell in the week to compute KW
            const ref = cells.find((c) => c) || new Date(cursor);
            rows.push({ kw: isoWeekNumber(ref), cells });
        }
        return rows;
    }, [grid, cursor]);

    const dateKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

    const eventsByDate = useMemo(() => {
        const map = {};
        const add = (key, item) => {
            if (filters.show[item.kind] === false) return;
            (map[key] = map[key] || []).push(item);
        };

        // Tasks (with deadline)
        tasks.forEach((t) => {
            if (!t.deadline) return;
            const d = new Date(t.deadline);
            add(dateKey(d), { ...t, kind: "task" });
        });

        // Invoices
        invoices.forEach((iv) => {
            const d = new Date(iv.due_date);
            add(dateKey(d), { ...iv, kind: "invoice" });
        });

        // Calendar events — may span multiple days
        events.forEach((ev) => {
            const s = new Date(ev.start);
            const e = ev.end ? new Date(ev.end) : new Date(s);
            // Iterate days in range
            let cur = new Date(s.getFullYear(), s.getMonth(), s.getDate());
            const endDay = new Date(e.getFullYear(), e.getMonth(), e.getDate());
            while (cur <= endDay) {
                add(dateKey(cur), { ...ev, kind: "event" });
                cur.setDate(cur.getDate() + 1);
            }
        });

        // Private todos (with deadline) — only for current user (already filtered server-side)
        todos.forEach((td) => {
            if (!td.deadline) return;
            const d = new Date(td.deadline);
            add(dateKey(d), { ...td, kind: "todo" });
        });

        // On-call (multi-day) — visible to all
        onCall.forEach((oc) => {
            const s = new Date(oc.start);
            const e = new Date(oc.end);
            let cur = new Date(s.getFullYear(), s.getMonth(), s.getDate());
            const endDay = new Date(e.getFullYear(), e.getMonth(), e.getDate());
            while (cur <= endDay) {
                add(dateKey(cur), { ...oc, kind: "oncall" });
                cur.setDate(cur.getDate() + 1);
            }
        });

        // Approved vacations
        vacations.forEach((vc) => {
            const s = new Date(vc.start_date);
            const e = new Date(vc.end_date);
            let cur = new Date(s.getFullYear(), s.getMonth(), s.getDate());
            const endDay = new Date(e.getFullYear(), e.getMonth(), e.getDate());
            while (cur <= endDay) {
                add(dateKey(cur), { ...vc, kind: "vacation" });
                cur.setDate(cur.getDate() + 1);
            }
        });

        // School holidays (optional, off by default). Always added when present,
        // ignoring the kind-filter — they only appear if at least 1 Bundesland is selected.
        schoolHolidays.forEach((h, i) => {
            const s = new Date(h.start);
            const e = new Date(h.end);
            let cur = new Date(s.getFullYear(), s.getMonth(), s.getDate());
            const endDay = new Date(e.getFullYear(), e.getMonth(), e.getDate());
            const id = `${h.state}-${h.start}-${i}`;
            while (cur <= endDay) {
                (map[dateKey(cur)] = map[dateKey(cur)] || []).push({ ...h, id, kind: "holiday" });
                cur.setDate(cur.getDate() + 1);
            }
        });

        // Public holidays per country (e.g. Greece — gesetzliche Feiertage).
        publicHolidays.forEach((h, i) => {
            const s = new Date(h.start);
            const e = new Date(h.end || h.start);
            let cur = new Date(s.getFullYear(), s.getMonth(), s.getDate());
            const endDay = new Date(e.getFullYear(), e.getMonth(), e.getDate());
            const id = `ph-${h.country}-${h.start}-${i}`;
            while (cur <= endDay) {
                (map[dateKey(cur)] = map[dateKey(cur)] || []).push({ ...h, id, kind: "public_holiday" });
                cur.setDate(cur.getDate() + 1);
            }
        });

        // Apply consistent sort: Bereitschaft first, then by KIND_ORDER
        Object.keys(map).forEach((k) => {
            map[k].sort((a, b) => itemSortValue(a) - itemSortValue(b));
        });
        return map;
    }, [tasks, invoices, events, todos, onCall, vacations, schoolHolidays, publicHolidays, filters.show]);

    const today = new Date();
    const isSameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

    const openNewEventForDate = (date) => {
        setEventPresetDate(date);
        setNewEventOpen(true);
    };

    return (
        <div className="p-4 sm:p-6 md:p-8 space-y-6" data-testid="calendar-page">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500 mb-1">{t(lang, "calendar")}</p>
                    <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-neutral-900">{monthLabel}</h1>
                </div>
                <div className="flex gap-2 items-center flex-wrap">
                    <Button onClick={() => { setEventPresetDate(null); setNewEventOpen(true); }} data-testid="new-event-btn" className="bg-neutral-900 hover:bg-neutral-800">
                        <Plus className="w-4 h-4 mr-1" /> {t(lang, "newEvent")}
                    </Button>
                    {isPriv && (
                        <Button onClick={() => setNewOnCallOpen(true)} variant="outline" data-testid="new-oncall-btn">
                            <Siren className="w-4 h-4 mr-1" /> {t(lang, "newOnCall")}
                        </Button>
                    )}
                    <OnCallSwapInbox currentUserId={user?.id} lang={lang} onChanged={load} />
                    <Select value={spaceFilter} onValueChange={setSpaceFilter}>
                        <SelectTrigger className="w-44" data-testid="calendar-space-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all" data-testid="cal-filter-all">{t(lang, "allSpaces")}</SelectItem>
                            {spaces.map((s) => (
                                <SelectItem key={s.id} value={s.id} data-testid={`cal-filter-${s.id}`}>{s.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Popover open={filterOpen} onOpenChange={setFilterOpen}>
                        <PopoverTrigger asChild>
                            <Button variant="outline" data-testid="calendar-filters-btn" className="gap-1.5">
                                <Filter className="w-3.5 h-3.5" />
                                {lang === "de" ? "Filter" : "Filters"}
                                {(Object.values(filters.show).some((v) => !v) || filters.states.length > 0 || filters.countries.length > 0) && (
                                    <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] rounded-full bg-brand-500 text-white font-semibold">
                                        {Object.values(filters.show).filter((v) => !v).length + filters.states.length + filters.countries.length}
                                    </span>
                                )}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80 max-h-[80vh] overflow-y-auto" align="end">
                            <div className="space-y-4">
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
                                            {lang === "de" ? "Anzeigen" : "Visible kinds"}
                                        </p>
                                    </div>
                                    <div className="space-y-1.5 text-sm">
                                        {[
                                            { k: "oncall",   l: lang === "de" ? "Bereitschaft" : "On-call",  icon: <Siren className="w-3.5 h-3.5 text-red-600" /> },
                                            { k: "vacation", l: lang === "de" ? "Urlaub" : "Vacation",       icon: <Plane className="w-3.5 h-3.5 text-amber-600" /> },
                                            { k: "event",    l: lang === "de" ? "Termine" : "Events",        icon: <span className="w-3 h-3 rounded bg-blue-500 inline-block" /> },
                                            { k: "task",     l: lang === "de" ? "Aufgaben" : "Tasks",        icon: <span className="w-3 h-3 rounded bg-neutral-900 inline-block" /> },
                                            { k: "todo",     l: lang === "de" ? "Meine ToDos" : "My todos",  icon: <span className="w-3 h-3 rounded bg-emerald-500 inline-block" /> },
                                            { k: "invoice",  l: lang === "de" ? "Banking / Rechnungen" : "Banking", icon: <span className="text-sm leading-none">💶</span> },
                                        ].map(({ k, l, icon }) => (
                                            <label key={k} className="flex items-center gap-2 cursor-pointer py-0.5 px-1 -mx-1 rounded hover:bg-neutral-50" data-testid={`cal-filter-show-${k}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={!!filters.show[k]}
                                                    onChange={() => toggleKind(k)}
                                                    className="accent-brand-500"
                                                />
                                                <span className="w-4 inline-flex justify-center">{icon}</span>
                                                <span className="flex-1">{l}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div className="border-t border-neutral-200 pt-3">
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500 mb-2 flex items-center gap-1.5">
                                        <GraduationCap className="w-3.5 h-3.5" />
                                        {lang === "de" ? "Schulferien anzeigen" : "School holidays"}
                                    </p>
                                    <p className="text-[11px] text-neutral-500 mb-2">
                                        {lang === "de"
                                            ? "Wähle Bundesländer — die Schulferien werden gelb markiert. Standardmäßig aus."
                                            : "Pick states — school holidays will be highlighted yellow. Off by default."}
                                    </p>
                                    <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[12.5px]" data-testid="cal-filter-states">
                                        {BUNDESLAENDER.map((b) => (
                                            <label key={b.code} className="flex items-center gap-1.5 cursor-pointer py-0.5 px-1 rounded hover:bg-neutral-50">
                                                <input
                                                    type="checkbox"
                                                    checked={filters.states.includes(b.code)}
                                                    onChange={() => toggleState(b.code)}
                                                    className="accent-brand-500"
                                                    data-testid={`cal-filter-state-${b.code}`}
                                                />
                                                <span className="font-mono text-[10px] text-neutral-500 w-5">{b.code}</span>
                                                <span className="truncate">{b.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div className="border-t border-neutral-200 pt-3">
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500 mb-2 flex items-center gap-1.5">
                                        🌍 {lang === "de" ? "Feiertage Ausland" : "Public holidays (foreign)"}
                                    </p>
                                    <p className="text-[11px] text-neutral-500 mb-2">
                                        {lang === "de"
                                            ? "Aktiviere Länder, deren gesetzliche Feiertage du im Kalender sehen möchtest."
                                            : "Enable countries whose public holidays you'd like to see."}
                                    </p>
                                    <div className="space-y-1 text-sm" data-testid="cal-filter-countries">
                                        {PUBLIC_HOLIDAY_COUNTRIES.map((c) => (
                                            <label key={c.code} className="flex items-center gap-2 cursor-pointer py-0.5 px-1 -mx-1 rounded hover:bg-neutral-50">
                                                <input
                                                    type="checkbox"
                                                    checked={filters.countries.includes(c.code)}
                                                    onChange={() => toggleCountry(c.code)}
                                                    className="accent-brand-500"
                                                    data-testid={`cal-filter-country-${c.code}`}
                                                />
                                                <span className="text-base leading-none">{c.flag}</span>
                                                <span className="flex-1">{c.name}</span>
                                                <span className="text-[10px] font-mono text-neutral-400">{c.code}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div className="border-t border-neutral-200 pt-3 flex items-center justify-between">
                                    <button
                                        onClick={resetFilters}
                                        type="button"
                                        className="inline-flex items-center gap-1.5 text-xs text-neutral-600 hover:text-red-600 px-2 py-1 rounded hover:bg-neutral-50"
                                        data-testid="cal-filter-reset"
                                    >
                                        <RotateCcw className="w-3 h-3" /> {lang === "de" ? "Alle Filter zurücksetzen" : "Reset all filters"}
                                    </button>
                                    <button
                                        onClick={() => setFilterOpen(false)}
                                        type="button"
                                        className="text-xs px-3 py-1 rounded bg-neutral-900 text-white hover:bg-neutral-800"
                                        data-testid="cal-filter-done"
                                    >
                                        {lang === "de" ? "Fertig" : "Done"}
                                    </button>
                                </div>
                            </div>
                        </PopoverContent>
                    </Popover>
                    <Button variant="outline" size="icon" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
                    <Button variant="outline" onClick={() => setCursor(new Date())} data-testid="cal-today">{lang === "tr" ? "Bugün" : lang === "de" ? "Heute" : "Today"}</Button>
                    <Button variant="outline" size="icon" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
                </div>
            </div>

            {/* Legend */}
            <div className="flex items-center gap-3 text-[11px] text-neutral-600 flex-wrap" data-testid="calendar-legend">
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-neutral-900" /> {t(lang, "tasks")}</span>
                <span className="inline-flex items-center gap-1">💶 {t(lang, "banking")}</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-500" /> {t(lang, "newEvent").replace(" ", "")}</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500" /> {t(lang, "myTodos")}</span>
                <span className="inline-flex items-center gap-1"><Siren className="w-3 h-3 text-red-600" /> {t(lang, "onCall")}</span>
                <span className="inline-flex items-center gap-1"><Plane className="w-3 h-3 text-amber-600" /> {t(lang, "vacation")}</span>
            </div>

            <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
                <div className="overflow-x-auto">
                <div className="min-w-[640px]">
                <div className="grid grid-cols-[44px_repeat(7,minmax(0,1fr))] border-b border-neutral-200 bg-neutral-50">
                    <div className="px-2 py-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-400 text-center border-r border-neutral-200">KW</div>
                    {dayNames.map((d) => (
                        <div key={d} className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-500 text-center">{d}</div>
                    ))}
                </div>
                <div>
                    {weekRows.map((row, rowIdx) => (
                        <div key={rowIdx} className="grid grid-cols-[44px_repeat(7,minmax(0,1fr))] border-b border-neutral-100">
                            <div className="border-r border-neutral-200 bg-neutral-50/60 flex items-start justify-center pt-2 text-[11px] font-semibold text-neutral-500" data-testid={`cal-kw-${row.kw}`}>
                                {row.kw}
                            </div>
                            {row.cells.map((d, i) => {
                                if (!d) return <div key={i} className="border-r border-neutral-100 bg-neutral-50/40 min-h-[110px]" />;
                                const key = dateKey(d);
                                const dayItems = eventsByDate[key] || [];
                                const isToday = isSameDay(d, today);
                                const isHoliday = dayItems.some((it) => it.kind === "holiday" || it.is_holiday);
                                const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                                return (
                                    <div
                                        key={i}
                                        data-testid={`cal-day-${d.getDate()}`}
                                        className={`border-r border-neutral-100 p-1.5 min-h-[110px] group relative cursor-pointer transition ${
                                            isToday ? "bg-amber-50/60" :
                                            isHoliday ? "bg-red-50/40" :
                                            isWeekend ? "bg-neutral-50/40" : ""
                                        } hover:bg-brand-50/30`}
                                        onClick={() => dayItems.length > 0 && setDayDetailDate(d)}
                                    >
                                        <div className="flex items-center justify-between mb-1">
                                            <span className={`text-xs font-semibold ${
                                                isToday ? "text-amber-700" :
                                                isHoliday ? "text-red-700" :
                                                isWeekend ? "text-neutral-500" :
                                                "text-neutral-700"
                                            }`}>{d.getDate()}</span>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); openNewEventForDate(d); }}
                                                data-testid={`cal-add-${d.getDate()}`}
                                                className="opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-neutral-900 transition"
                                                title={t(lang, "newEvent")}
                                            >
                                                <Plus className="w-3 h-3" />
                                            </button>
                                        </div>
                                        <div className="space-y-0.5" onClick={(e) => e.stopPropagation()}>
                                            {dayItems.slice(0, 4).map((it, idx) => (
                                                <CalendarItem
                                                    key={`${it.kind}-${it.id}-${idx}`}
                                                    item={it}
                                                    spaces={spaces}
                                                    onOpenTask={(id) => setOpenTaskId(id)}
                                                />
                                            ))}
                                            {dayItems.length > 4 && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setDayDetailDate(d); }}
                                                    className="w-full text-left text-[10px] text-brand-700 hover:text-brand-900 font-semibold px-1.5 py-0.5 rounded hover:bg-brand-50 transition"
                                                    data-testid={`cal-more-${d.getDate()}`}
                                                >
                                                    + {dayItems.length - 4} {lang === "de" ? "weitere…" : "more…"}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
                </div>
                </div>
            </div>

            <TaskDetailDialog
                taskId={openTaskId}
                open={!!openTaskId}
                onClose={() => setOpenTaskId(null)}
                onChange={load}
                allUsers={users}
                spaces={spaces}
                onOpenTask={(id) => setOpenTaskId(id)}
            />
            <DayDetailDialog
                date={dayDetailDate}
                items={dayDetailDate ? (eventsByDate[dateKey(dayDetailDate)] || []) : []}
                onClose={() => setDayDetailDate(null)}
                onOpenTask={(id) => { setDayDetailDate(null); setOpenTaskId(id); }}
                onAddEvent={(d) => { setDayDetailDate(null); openNewEventForDate(d); }}
                onEditOnCall={(oc) => { setDayDetailDate(null); setEditOnCall(oc); }}
                onSwapOnCall={(oc) => { setDayDetailDate(null); setSwapOnCall(oc); }}
                currentUser={user}
                isPriv={isPriv}
                spaces={spaces}
                lang={lang}
                months={months}
            />
            {editOnCall && (
                <OnCallEditDialog
                    oncall={editOnCall}
                    users={users}
                    onClose={() => setEditOnCall(null)}
                    onChanged={load}
                    lang={lang}
                />
            )}
            {swapOnCall && (
                <OnCallSwapDialog
                    oncall={swapOnCall}
                    users={users}
                    currentUserId={user?.id}
                    onClose={() => setSwapOnCall(null)}
                    onSubmitted={load}
                    lang={lang}
                />
            )}
            <NewEventDialog
                open={newEventOpen}
                onClose={() => setNewEventOpen(false)}
                onCreated={load}
                presetDate={eventPresetDate}
                spaces={spaces}
                user={user}
                lang={lang}
            />
            <NewOnCallDialog
                open={newOnCallOpen}
                onClose={() => setNewOnCallOpen(false)}
                onCreated={load}
                employees={users}
                lang={lang}
            />
        </div>
    );
}

function DayDetailDialog({ date, items, onClose, onOpenTask, onAddEvent, onEditOnCall, onSwapOnCall, currentUser, isPriv, spaces, lang, months }) {
    if (!date) return null;
    const open = !!date;
    const dateLabel = `${date.getDate()}. ${months[date.getMonth()]} ${date.getFullYear()}`;
    const KIND_LABELS = {
        oncall:         { de: "Bereitschaft",       en: "On-call" },
        vacation:       { de: "Urlaub",             en: "Vacation" },
        public_holiday: { de: "Feiertag",           en: "Public holiday" },
        event:          { de: "Termin",             en: "Event" },
        task:           { de: "Aufgabe",            en: "Task" },
        todo:           { de: "Mein ToDo",          en: "My todo" },
        invoice:        { de: "Rechnung",           en: "Invoice" },
        holiday:        { de: "Schulferien",        en: "School holiday" },
    };
    // Group by kind for an organised list
    const grouped = items.reduce((acc, it) => {
        (acc[it.kind] = acc[it.kind] || []).push(it);
        return acc;
    }, {});
    const orderKeys = Object.keys(grouped).sort((a, b) => (KIND_ORDER[a] ?? 99) - (KIND_ORDER[b] ?? 99));

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="cal-day-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl tracking-tight">{dateLabel}</DialogTitle>
                    <DialogDescription>
                        {items.length} {lang === "de" ? (items.length === 1 ? "Eintrag" : "Einträge") : (items.length === 1 ? "entry" : "entries")}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 mt-2">
                    {orderKeys.length === 0 ? (
                        <p className="text-sm text-neutral-500 text-center py-6">{lang === "de" ? "Keine Einträge an diesem Tag." : "No entries on this day."}</p>
                    ) : orderKeys.map((kind) => (
                        <div key={kind}>
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500 mb-1">
                                {(KIND_LABELS[kind] || {})[lang === "de" ? "de" : "en"] || kind}
                                <span className="ml-1 text-neutral-400">· {grouped[kind].length}</span>
                            </p>
                            <div className="space-y-1.5">
                                {grouped[kind].map((it, idx) => (
                                    <DayItemRow key={`${kind}-${it.id}-${idx}`} item={it} spaces={spaces} onOpenTask={onOpenTask} lang={lang}
                                        onEditOnCall={onEditOnCall} onSwapOnCall={onSwapOnCall} currentUser={currentUser} isPriv={isPriv} />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
                <DialogFooter className="mt-4 gap-2">
                    <Button variant="outline" onClick={onClose} data-testid="cal-day-close">{lang === "de" ? "Schließen" : "Close"}</Button>
                    <Button onClick={() => onAddEvent(date)} data-testid="cal-day-add" className="bg-brand-600 hover:bg-brand-700 text-white">
                        <Plus className="w-4 h-4 mr-1" /> {lang === "de" ? "Termin hinzufügen" : "Add event"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function DayItemRow({ item, spaces, onOpenTask, lang, onEditOnCall, onSwapOnCall, currentUser, isPriv }) {
    const baseCls = "block w-full text-left p-2 rounded-md border bg-white hover:bg-neutral-50 transition";
    if (item.kind === "task") {
        const sp = spaces.find((s) => s.id === item.space_id);
        return (
            <button onClick={() => onOpenTask(item.id)} className={`${baseCls} border-neutral-200`} data-testid={`cal-day-task-${item.id}`}>
                <div className="flex items-center gap-2">
                    <span className="w-1 h-6 rounded" style={{ background: sp?.color || "#0A0A0A" }} />
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.title}</p>
                        {sp && <p className="text-[10px] text-neutral-500 truncate">{sp.name}</p>}
                    </div>
                </div>
            </button>
        );
    }
    if (item.kind === "invoice") {
        const paid = item.status === "paid";
        return (
            <a href={`/banking?invoice=${item.id}`} className={`${baseCls} border-amber-200`} data-testid={`cal-day-inv-${item.id}`}>
                <p className={`text-sm font-medium ${paid ? "line-through text-emerald-700" : ""}`}>💶 €{Number(item.amount || 0).toFixed(2)} · {item.title}</p>
                <p className="text-[10px] text-neutral-500">{lang === "de" ? (paid ? "Bezahlt" : "Offen") : (paid ? "Paid" : "Open")}</p>
            </a>
        );
    }
    if (item.kind === "event") {
        return (
            <div className={`${baseCls} border-blue-200`} data-testid={`cal-day-event-${item.id}`}>
                <p className="text-sm font-medium">{item.all_day ? "📅 " : "🕒 "}{item.title}</p>
                {item.description && <p className="text-[11px] text-neutral-600 mt-0.5 whitespace-pre-wrap">{item.description}</p>}
            </div>
        );
    }
    if (item.kind === "todo") {
        return (
            <div className={`${baseCls} border-emerald-200 ${item.completed ? "opacity-60" : ""}`} data-testid={`cal-day-todo-${item.id}`}>
                <p className={`text-sm ${item.completed ? "line-through" : ""}`}>✓ {item.title}</p>
            </div>
        );
    }
    if (item.kind === "oncall") {
        const isOwner = item.user_id === currentUser?.id;
        return (
            <div className={`${baseCls} border-red-300 bg-red-50/50`} data-testid={`cal-day-oncall-${item.id}`}>
                <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-red-800">🚨 Bereitschaft · {item.user_name}</p>
                        <p className="text-[10px] text-red-700">{item.start} → {item.end}</p>
                        {item.note && <p className="text-[11px] text-red-700 mt-0.5">{item.note}</p>}
                        {item.swapped_from_name && <p className="text-[10px] text-emerald-700 mt-0.5">↻ getauscht von {item.swapped_from_name}</p>}
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                        {isOwner && (
                            <button onClick={() => onSwapOnCall?.(item)} className="text-[10px] px-2 py-0.5 rounded bg-white border border-red-200 hover:bg-red-100 text-red-700 font-medium" data-testid={`oncall-swap-btn-${item.id}`}>
                                ↻ {lang === "de" ? "Tauschen" : "Swap"}
                            </button>
                        )}
                        {isPriv && (
                            <button onClick={() => onEditOnCall?.(item)} className="text-[10px] px-2 py-0.5 rounded bg-white border border-neutral-200 hover:bg-neutral-50 text-neutral-700 font-medium" data-testid={`oncall-edit-btn-${item.id}`}>
                                ✎ {lang === "de" ? "Bearbeiten" : "Edit"}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }
    if (item.kind === "vacation") {
        return (
            <div className={`${baseCls} border-amber-200 bg-amber-50/50`} data-testid={`cal-day-vac-${item.id}`}>
                <p className="text-sm font-medium text-amber-900">✈️ {item.user_name} <span className="text-[10px] font-normal">({item.start_date} → {item.end_date})</span></p>
                {item.reason && <p className="text-[11px] text-amber-700 mt-0.5">{item.reason}</p>}
            </div>
        );
    }
    if (item.kind === "holiday") {
        return (
            <div className={`${baseCls} border-yellow-300 bg-yellow-50`} data-testid={`cal-day-holiday-${item.state}-${item.start}`}>
                <p className="text-sm font-medium text-yellow-900">🎒 {item.name} <span className="text-[10px] font-normal">({item.state})</span></p>
                <p className="text-[10px] text-yellow-800">{item.start} → {item.end}</p>
            </div>
        );
    }
    if (item.kind === "public_holiday") {
        const flag = item.country === "GR" ? "🇬🇷" : item.country === "DE" ? "🇩🇪" : "🏳️";
        return (
            <div className={`${baseCls} border-rose-300 bg-rose-50`} data-testid={`cal-day-publicholiday-${item.country}-${item.start}`}>
                <p className="text-sm font-medium text-rose-900">{flag} {item.name}</p>
                {item.name_en && item.name_en !== item.name && <p className="text-[10px] text-rose-700">{item.name_en} · {item.country}</p>}
            </div>
        );
    }
    return null;
}

function CalendarItem({ item, spaces, onOpenTask }) {
    if (item.kind === "task") {
        const sp = spaces.find((s) => s.id === item.space_id);
        return (
            <button onClick={() => onOpenTask(item.id)} data-testid={`cal-event-${item.id}`} className="w-full text-left text-[11px] truncate px-1.5 py-0.5 rounded hover:bg-neutral-100 border-l-2" style={{ borderLeftColor: sp?.color || "#0A0A0A" }}>
                {item.title}
            </button>
        );
    }
    if (item.kind === "invoice") {
        const paid = item.status === "paid";
        return (
            <button onClick={() => { window.location.href = `/banking?invoice=${item.id}`; }} data-testid={`cal-event-${item.id}`} className={`w-full text-left text-[11px] truncate px-1.5 py-0.5 rounded hover:bg-neutral-100 border-l-2 ${paid ? "line-through text-emerald-700" : ""}`} style={{ borderLeftColor: paid ? "#10b981" : "#f59e0b" }}>
                💶 €{Number(item.amount || 0).toFixed(0)} · {item.title}
            </button>
        );
    }
    if (item.kind === "event") {
        // Hessian holidays get a distinct red look
        if (item.is_holiday) {
            return (
                <div className="text-[11px] truncate px-1.5 py-0.5 rounded bg-red-100 text-red-800 font-semibold border border-red-200" title={item.title} data-testid={`cal-holiday-${item.id}`}>
                    {item.title}
                </div>
            );
        }
        if (item.is_birthday) {
            return (
                <div className="text-[11px] truncate px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200" title={item.title} data-testid={`cal-birthday-${item.id}`}>
                    {item.title}
                </div>
            );
        }
        return (
            <div className="text-[11px] truncate px-1.5 py-0.5 rounded border-l-2 bg-white" style={{ borderLeftColor: EVENT_COLORS[item.color] || EVENT_COLORS.blue }} title={item.title} data-testid={`cal-event-${item.id}`}>
                {item.all_day ? "📅 " : "🕒 "}{item.title}
            </div>
        );
    }
    if (item.kind === "todo") {
        return (
            <div className={`text-[11px] truncate px-1.5 py-0.5 rounded border-l-2 ${item.completed ? "line-through text-neutral-400" : ""}`} style={{ borderLeftColor: "#10b981" }} title={item.title} data-testid={`cal-todo-${item.id}`}>
                ✓ {item.title}
            </div>
        );
    }
    if (item.kind === "oncall") {
        return (
            <div className="text-[11px] truncate px-1.5 py-0.5 rounded bg-red-50 text-red-800 border border-red-200" title={`Bereitschaft: ${item.user_name}`} data-testid={`cal-oncall-${item.id}`}>
                🚨 {item.user_name}
            </div>
        );
    }
    if (item.kind === "vacation") {
        return (
            <div className="text-[11px] truncate px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200" title={`Urlaub: ${item.user_name}`} data-testid={`cal-vacation-${item.id}`}>
                ✈️ {item.user_name}
            </div>
        );
    }
    if (item.kind === "holiday") {
        return (
            <div className="text-[11px] truncate px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-900 border border-yellow-300 font-medium" title={`${item.name} (${item.state})`} data-testid={`cal-school-${item.state}-${item.start}`}>
                🎒 {item.name} <span className="text-[9px] opacity-70">({item.state})</span>
            </div>
        );
    }
    if (item.kind === "public_holiday") {
        const flag = item.country === "GR" ? "🇬🇷" : item.country === "DE" ? "🇩🇪" : "🏳️";
        return (
            <div className="text-[11px] truncate px-1.5 py-0.5 rounded bg-rose-50 text-rose-900 border border-rose-300 font-medium" title={`${item.name}${item.name_en ? ` (${item.name_en})` : ""} · ${item.country}`} data-testid={`cal-publicholiday-${item.country}-${item.start}`}>
                {flag} {item.name}
            </div>
        );
    }
    return null;
}

function NewEventDialog({ open, onClose, onCreated, presetDate, spaces, user, lang }) {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [allDay, setAllDay] = useState(true);
    const [color, setColor] = useState("blue");
    const [visibility, setVisibility] = useState("personal");
    const [spaceId, setSpaceId] = useState("");
    const [location, setLocation] = useState("");
    const [busy, setBusy] = useState(false);

    const isPriv = user?.role === "admin" || user?.role === "master";

    useEffect(() => {
        if (open) {
            setTitle(""); setDescription(""); setLocation(""); setColor("blue"); setVisibility("personal"); setSpaceId("");
            setAllDay(true);
            if (presetDate) {
                const d = new Date(presetDate);
                const iso = d.toISOString().slice(0, 10);
                setStart(iso);
                setEnd(iso);
            } else {
                const t = new Date();
                setStart(t.toISOString().slice(0, 10));
                setEnd(t.toISOString().slice(0, 10));
            }
        }
    }, [open, presetDate]);

    const submit = async () => {
        if (!title.trim() || !start) return;
        setBusy(true);
        try {
            const payload = {
                title,
                description,
                start: allDay ? new Date(start + "T00:00:00").toISOString() : new Date(start).toISOString(),
                end: end ? (allDay ? new Date(end + "T23:59:59").toISOString() : new Date(end).toISOString()) : null,
                all_day: allDay,
                location,
                color,
                visibility,
                space_id: visibility === "team" ? spaceId : null,
            };
            await api.post("/calendar/events", payload);
            toast.success(t(lang, "create"));
            onCreated();
            onClose();
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="new-event-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">{t(lang, "newEvent")}</DialogTitle>
                    <DialogDescription className="sr-only">Event</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "eventTitle")}</Label>
                        <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1.5" data-testid="event-title" autoFocus />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "description")}</Label>
                        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="mt-1.5" data-testid="event-desc" />
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} data-testid="event-allday" className="w-4 h-4" />
                        <span>{t(lang, "allDay")}</span>
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "startDate")}</Label>
                            <Input type={allDay ? "date" : "datetime-local"} value={start} onChange={(e) => setStart(e.target.value)} data-testid="event-start" className="mt-1.5" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "endDate")}</Label>
                            <Input type={allDay ? "date" : "datetime-local"} value={end} onChange={(e) => setEnd(e.target.value)} data-testid="event-end" className="mt-1.5" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "visibility")}</Label>
                            <Select value={visibility} onValueChange={setVisibility}>
                                <SelectTrigger className="mt-1.5" data-testid="event-visibility"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="personal">{t(lang, "personal")}</SelectItem>
                                    <SelectItem value="team">{t(lang, "team")}</SelectItem>
                                    {isPriv && <SelectItem value="company">{t(lang, "company")}</SelectItem>}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "color")}</Label>
                            <Select value={color} onValueChange={setColor}>
                                <SelectTrigger className="mt-1.5" data-testid="event-color"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {Object.keys(EVENT_COLORS).map((c) => (
                                        <SelectItem key={c} value={c}>
                                            <span className="inline-flex items-center gap-2">
                                                <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: EVENT_COLORS[c] }} />
                                                {c}
                                            </span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    {visibility === "team" && (
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "team")}</Label>
                            <Select value={spaceId} onValueChange={setSpaceId}>
                                <SelectTrigger className="mt-1.5" data-testid="event-space"><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>
                                    {spaces.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "location")}</Label>
                        <Input value={location} onChange={(e) => setLocation(e.target.value)} className="mt-1.5" data-testid="event-location" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{t(lang, "cancel")}</Button>
                    <Button onClick={submit} disabled={busy || !title || !start || (visibility === "team" && !spaceId)} data-testid="event-create-btn" className="bg-neutral-900 hover:bg-neutral-800">
                        {t(lang, "create")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function NewOnCallDialog({ open, onClose, onCreated, employees, lang }) {
    const [userId, setUserId] = useState("");
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) { setUserId(""); setNote(""); setStart(""); setEnd(""); }
    }, [open]);

    const submit = async () => {
        if (!userId || !start || !end) return;
        setBusy(true);
        try {
            await api.post("/on-call", {
                user_id: userId,
                start: new Date(start).toISOString(),
                end: new Date(end).toISOString(),
                note,
            });
            toast.success(t(lang, "create"));
            onCreated();
            onClose();
        } catch (e) { toast.error(errMsg(e, "Error")); } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="new-oncall-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2"><Siren className="w-5 h-5 text-red-600" /> {t(lang, "newOnCall")}</DialogTitle>
                    <DialogDescription className="sr-only">Bereitschaft</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "selectEmployee")}</Label>
                        <Select value={userId} onValueChange={setUserId}>
                            <SelectTrigger className="mt-1.5" data-testid="oncall-user"><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent>
                                {employees.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "startDate")}</Label>
                            <Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} data-testid="oncall-start" className="mt-1.5" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "endDate")}</Label>
                            <Input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="oncall-end" className="mt-1.5" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{lang === "de" ? "Notiz" : "Note"}</Label>
                        <Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5" data-testid="oncall-note" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{t(lang, "cancel")}</Button>
                    <Button onClick={submit} disabled={busy || !userId || !start || !end} data-testid="oncall-create-btn" className="bg-neutral-900 hover:bg-neutral-800">
                        {t(lang, "create")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
